/**
 * Two-namespace interpolation for preset item config.
 *
 * Syntax:
 *   - `${INPUT:KEY}` — placeholder value inlined into Instance.config
 *   - `${SECRET:KEY}` — stored as credential_ref in Instance; value is handed
 *     to SecretsRouter.setSecret separately
 *
 * The `materialize` map on a preset item defines how placeholder values fold
 * into the Instance config JSON. Each key is a dotted config path; each value
 * is a template string that may contain ${INPUT:KEY} or ${SECRET:KEY} tokens.
 *
 * Constraints:
 *   - `${SECRET:KEY}` MUST be the sole token in a template (no mixing with
 *     INPUT or literal text). Mixed templates would embed non-secret data
 *     into the stored secret, breaking update semantics.
 *   - All referenced keys MUST exist in the context. Missing keys throw
 *     InterpolationError rather than silently producing empty strings.
 *
 * Example:
 * ```yaml
 * materialize:
 *   host: "${INPUT:HOST}"
 *   port: "${INPUT:PORT}"
 *   credentials.api_key: "${SECRET:API_KEY}"
 * ```
 *
 * @docLink packages/library/concepts#interpolation
 */

import type { SecretsRouter } from "@skaile/workspaces/secrets";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Runtime context passed to {@link interpolate} containing resolved placeholder values.
 *
 * @docLink packages/library/concepts#interpolation
 */
export interface InterpolationContext {
  /** Validated placeholder values (key to value). */
  inputs: Record<string, unknown>;
  /** Secret values (key to plaintext value) to be stored via SecretsRouter. */
  secrets: Record<string, string>;
}

/**
 * Result produced by {@link interpolate}.
 *
 * @docLink packages/library/concepts#interpolation
 */
export interface InterpolationResult {
  /** The materialized config to set on the Instance. */
  config: Record<string, unknown>;
  /** Credential ref to set on the Instance (if any secret placeholders used). */
  credentialRef?: string;
  /** Secret entries that need to be stored (ref to plaintext value). */
  secretsToStore: Array<{ ref: string; value: string }>;
}

/**
 * Thrown when a template references a missing key or mixes `${SECRET:...}` with other tokens.
 *
 * @docLink packages/library/concepts#interpolation
 */
export class InterpolationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InterpolationError";
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Interpolate a materialize map with placeholder values.
 *
 * @param materialize - Map of dotted config paths to template strings.
 * @param baseConfig - Pre-existing item config (author-prefilled values).
 * @param context - Validated input values and secret values.
 * @param credentialRefPrefix - Prefix for generated credential refs.
 * @returns Interpolated config and secrets to store.
 * @throws {InterpolationError} if a referenced key is missing or a SECRET token
 *   is mixed with other tokens in the same template.
 * @docLink packages/library/concepts#interpolation
 */
export function interpolate(
  materialize: Record<string, string> | undefined,
  baseConfig: Record<string, unknown> | undefined,
  context: InterpolationContext,
  credentialRefPrefix: string,
): InterpolationResult {
  const config: Record<string, unknown> = { ...(baseConfig ?? {}) };
  const secretsToStore: Array<{ ref: string; value: string }> = [];
  let credentialRef: string | undefined;

  if (!materialize) {
    // No materialize map: merge inputs directly into config (flat)
    for (const [key, value] of Object.entries(context.inputs)) {
      config[key] = value;
    }
    // If there are secrets, create a credential ref
    if (Object.keys(context.secrets).length > 0) {
      credentialRef = `${credentialRefPrefix}/creds`;
      for (const [key, value] of Object.entries(context.secrets)) {
        secretsToStore.push({ ref: `${credentialRef}/${key}`, value });
      }
    }
    return { config, credentialRef, secretsToStore };
  }

  // Process materialize map
  for (const [configPath, template] of Object.entries(materialize)) {
    const resolved = resolveTemplate(template, context);

    if (resolved.hasSecret) {
      // This config path holds a secret reference — don't inline the value
      if (!credentialRef) {
        credentialRef = `${credentialRefPrefix}/creds`;
      }
      const secretRef = `${credentialRef}/${configPath}`;
      secretsToStore.push({ ref: secretRef, value: resolved.value as string });
      // Store the ref path in config so the runtime knows where to look
      setNestedValue(config, configPath, `secret://${secretRef}`);
    } else {
      setNestedValue(config, configPath, resolved.value);
    }
  }

  return { config, credentialRef, secretsToStore };
}

/**
 * Store interpolated secrets via the SecretsRouter.
 *
 * @param router - The SecretsRouter instance.
 * @param secretsToStore - Array of `{ref, value}` pairs from {@link InterpolationResult}.
 * @docLink packages/library/concepts#interpolation
 */
export async function storeSecrets(
  router: SecretsRouter,
  secretsToStore: Array<{ ref: string; value: string }>,
): Promise<void> {
  for (const { ref, value } of secretsToStore) {
    const encoded = new TextEncoder().encode(value);
    await router.setSecret(ref, encoded);
  }
}

/**
 * Check if a template string contains any interpolation tokens (`${INPUT:...}` or `${SECRET:...}`).
 *
 * @param template - The template string to inspect.
 * @returns `true` if at least one token is present.
 * @docLink packages/library/concepts#interpolation
 */
export function hasInterpolationTokens(template: string): boolean {
  return INTERPOLATION_PATTERN.test(template);
}

/**
 * Extract all placeholder keys referenced in a materialize map.
 *
 * @param materialize - Map of dotted config paths to template strings.
 * @returns Sets of unique `INPUT` and `SECRET` key names referenced across all templates.
 * @docLink packages/library/concepts#interpolation
 */
export function extractReferencedKeys(materialize: Record<string, string>): {
  inputKeys: string[];
  secretKeys: string[];
} {
  const inputKeys = new Set<string>();
  const secretKeys = new Set<string>();

  for (const template of Object.values(materialize)) {
    const pattern = new RegExp(INTERPOLATION_PATTERN.source, "g");
    for (const match of template.matchAll(pattern)) {
      const namespace = match[1];
      const key = match[2];
      if (namespace === "INPUT") inputKeys.add(key);
      else if (namespace === "SECRET") secretKeys.add(key);
    }
  }

  return { inputKeys: [...inputKeys], secretKeys: [...secretKeys] };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Pattern: ${INPUT:KEY} or ${SECRET:KEY} */
const INTERPOLATION_PATTERN = /\$\{(INPUT|SECRET):([^}]+)\}/;

interface TemplateResolution {
  value: unknown;
  hasSecret: boolean;
}

function resolveTemplate(template: string, context: InterpolationContext): TemplateResolution {
  // Check if the entire template is a single token (preserve type)
  const singleMatch = template.match(/^\$\{(INPUT|SECRET):([^}]+)\}$/);
  if (singleMatch) {
    const [, namespace, key] = singleMatch;
    if (namespace === "SECRET") {
      if (!(key in context.secrets)) {
        throw new InterpolationError(`Missing secret key "${key}" in interpolation context`);
      }
      return { value: context.secrets[key], hasSecret: true };
    }
    // INPUT — return the typed value directly
    if (!(key in context.inputs)) {
      throw new InterpolationError(`Missing input key "${key}" in interpolation context`);
    }
    return { value: context.inputs[key], hasSecret: false };
  }

  // Multi-token template — reject if it contains any SECRET tokens.
  // Mixing secrets with other data in a compound string breaks update
  // semantics (updating the non-secret part later won't update the stored
  // secret) and leaks non-secret data into the secret store.
  const tokens = [...template.matchAll(/\$\{(INPUT|SECRET):([^}]+)\}/g)];
  const hasSecretToken = tokens.some(([, ns]) => ns === "SECRET");
  if (hasSecretToken) {
    throw new InterpolationError(
      `Template "${template}" mixes \${SECRET:...} with other tokens. ` +
        `SECRET must be the sole token in a template value.`,
    );
  }

  // Pure INPUT multi-token — string interpolation
  const result = template.replace(/\$\{INPUT:([^}]+)\}/g, (_, key) => {
    if (!(key in context.inputs)) {
      throw new InterpolationError(`Missing input key "${key}" in interpolation context`);
    }
    return String(context.inputs[key] ?? "");
  });

  return { value: result, hasSecret: false };
}

/**
 * Set a value at a dotted path in an object.
 * Creates intermediate objects as needed.
 *
 * Example: setNestedValue(obj, "a.b.c", 42) → obj.a.b.c = 42
 */
function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let current: Record<string, unknown> = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (current[key] === undefined || current[key] === null || typeof current[key] !== "object") {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[parts[parts.length - 1]] = value;
}
